import { HTMLAttributes, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import "./styles/Dialog.scss";
import ButtonClose from "@/components/ui/Buttons/ButtonClose/ButtonClose.tsx";
import DialogContext from "@/features/dialog/context/DialogContext.tsx";
import Title from "@/components/ui/Title/Title.tsx";
import { useTranslation } from "react-i18next";

type Props = HTMLAttributes<HTMLDialogElement> & {
  title?: string;
  type?: `default` | `success` | `error`;
  open?: boolean;
  fullscreen?: boolean;
  animateSize?: boolean;
  onClose: () => void;
  // Return true when the browser back button was handled, the dialog then stays open.
  onBrowserBack?: () => boolean;
  children: ReactNode;
}

const CLASS_PREFIX = `ws-widgets-dialog`;
const CLASS_BACKDROP = `js-ws-widgets-backdrop`;
const SIZE_ANIMATION_DURATION_MS = 200;
const CLASS_RESIZING = `${ CLASS_PREFIX }--resizing`;

type Size = {
  width: number;
  height: number;
}

const Dialog = ({ title, open = false, onClose, onBrowserBack, type = `default`, fullscreen = true, animateSize = false, className, children, ...rest }: Props) => {
  const ref = useRef<HTMLDialogElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const titlebarContainerRef = useRef<HTMLDivElement | null>(null);
  const [dialogAnimationClass, setDialogdialogAnimationClass] = useState(`ws-widgets-animation-disappear`);
  const [backdropAnimationClass, setBackdropAnimationClass] = useState(`ws-widgets-animation-fade-out`);
  const { t } = useTranslation();
  const historyStatePushed = useRef(false);
  const closingProgrammatically = useRef(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Expose the rendered titlebar height as a CSS variable.
  useEffect(() => {
    const dialog = ref.current;
    const titlebarContainer = titlebarContainerRef.current;
    if (!dialog || !titlebarContainer) {
      return;
    }

    const updateHeight = () => {
      dialog.style.setProperty(`--ws-dialog-titlebar-height`, `${ titlebarContainer.offsetHeight }px`);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(titlebarContainer);
    return () => resizeObserver.disconnect();
  }, []);

  // Runs after layout and before paint, so the new size never flashes
  // before the animation from the previous size starts.
  useEffect(() => {
    const box = boxRef.current;
    if (!animateSize || !box) {
      return;
    }

    let previousSize: Size | null = null;
    let animation: Animation | null = null;

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (animation?.playState === `running`) {
        return;
      }

      const size = {
        width: entry.borderBoxSize[0].inlineSize,
        height: entry.borderBoxSize[0].blockSize,
      };
      const from = previousSize;
      previousSize = size;

      const isHidden = !from?.width || !from.height || !size.width || !size.height;
      const isUnchanged = from?.width === size.width && from?.height === size.height;
      const prefersReducedMotion = window.matchMedia(`(prefers-reduced-motion: reduce)`).matches;
      if (!from || isHidden || isUnchanged || prefersReducedMotion) {
        return;
      }

      box.classList.add(CLASS_RESIZING);
      animation = box.animate([
        { width: `${ from.width }px`, height: `${ from.height }px` },
        { width: `${ size.width }px`, height: `${ size.height }px` },
      ], { duration: SIZE_ANIMATION_DURATION_MS, easing: `ease-in-out` });
      animation.onfinish = animation.oncancel = () => box.classList.remove(CLASS_RESIZING);
    });

    resizeObserver.observe(box);
    return () => {
      resizeObserver.disconnect();
      animation?.cancel();
    };
  }, [animateSize]);

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog) {
      return;
    }

    if (open) {
      // Cancel a pending close from the fade-out animation below.
      clearTimeout(closeTimeout.current);

      // showModal() throws if the dialog is already open.
      if (!dialog.open) {
        history.pushState({ wsDialogOpen: true }, ``);
        historyStatePushed.current = true;
        dialog.showModal();
      }
      setDialogdialogAnimationClass(``);
      requestAnimationFrame(() => {
        setBackdropAnimationClass(`ws-widgets-animation-fade-in`);
        setDialogdialogAnimationClass(`ws-widgets-animation-appear`);
      })
    } else if (dialog.open) {
      if (historyStatePushed.current) {
        historyStatePushed.current = false;
        closingProgrammatically.current = true;
        history.back();
      }
      setBackdropAnimationClass(`ws-widgets-animation-fade-out`);
      setDialogdialogAnimationClass(`ws-widgets-animation-disappear`);
      closeTimeout.current = setTimeout(() => {
        dialog?.close();
      }, 250);
    }
  }, [open]);

  // Make sure the backbutton closes the dialog instead of leaving the page.
  const handlePopstate = useCallback((event: PopStateEvent) => {
    if (!historyStatePushed.current && !closingProgrammatically.current) {
      return;
    }

    event.stopImmediatePropagation();

    if (closingProgrammatically.current) {
      historyStatePushed.current = false;
      closingProgrammatically.current = false;
      return;
    }

    if (onBrowserBack?.()) {
      history.pushState({ wsDialogOpen: true }, ``);
      return;
    }

    historyStatePushed.current = false;
    onClose();
  }, [onClose, onBrowserBack]);

  useEffect(() => {
    window.addEventListener(`popstate`, handlePopstate, { capture: true });
    return () => window.removeEventListener(`popstate`, handlePopstate, { capture: true });
  }, [handlePopstate]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };

    dialog.addEventListener(`cancel`, handleCancel);
    return () => dialog.removeEventListener(`cancel`, handleCancel);
  }, [onClose]);

  const handleClickDialog = (event: React.MouseEvent) => {
    const dialog = ref.current;

    if (!dialog) {
      return;
    }

    const isBackdrop = (event.target as HTMLObjectElement).classList.contains(CLASS_BACKDROP);
    if (isBackdrop) {
      onClose();
    }
  }

  const wrapperClasses = [
    `${ CLASS_PREFIX }-wrapper`,
    `${ CLASS_PREFIX }-wrapper--${ open ? `visible` : `` }`,
    `${ CLASS_PREFIX }-wrapper--${ type }`,
    `${ CLASS_PREFIX }-wrapper--${ fullscreen ? `fullscreen` : `not-fullscreen` }`,
    className ?? ``,
  ];

  const dialogClasses = [
    CLASS_PREFIX,
    `${ CLASS_PREFIX }--${ type }`,
    `${ CLASS_PREFIX }--${ fullscreen ? `fullscreen` : `not-fullscreen` }`,
    dialogAnimationClass,
  ];

  const backdropClasses = [
    CLASS_BACKDROP,
    `${ CLASS_PREFIX }-backdrop`,
    backdropAnimationClass,
  ];

  const titlebarClasses = [
    `${ CLASS_PREFIX }__titlebar`,
    `${ CLASS_PREFIX }__titlebar--${ type }`,
    `${ CLASS_PREFIX }__titlebar--${ fullscreen ? `fullscreen` : `not-fullscreen` }`,
    `${ CLASS_PREFIX }-max-width`,
  ];

  const titlebarContainerClasses = [
    `${ CLASS_PREFIX }__titlebar-container`,
    `${ CLASS_PREFIX }__titlebar-container--${ type }`,
  ];

  const contentClasses = [
    `${ CLASS_PREFIX }__content`,
    `${ CLASS_PREFIX }__content--${ fullscreen ? `fullscreen` : `not-fullscreen` }`,
    `${ CLASS_PREFIX }-max-width`,
  ];

  const contentContainerClasses = [
    `${ CLASS_PREFIX }__content-container`,
    `${ CLASS_PREFIX }__content-container--${ type }`,
    `${ CLASS_PREFIX }__content-container--${ fullscreen ? `fullscreen` : `not-fullscreen` }`,
  ];

  if (!title && type !== `default`) {
    title = t(`core.${ type }`);
  }

  return (
    <dialog
      { ...rest }
      ref={ ref }
      onClick={ handleClickDialog }
      className={ wrapperClasses.join(` `) }
    >
      <DialogContext.Provider value={ { isDialogOpen: open, isInDialog: true } }>
        <div className={ backdropClasses.join(` `) }/>
        <div ref={ boxRef } className={ dialogClasses.join(` `) }>
          <div className={ contentContainerClasses.join(` `) }>
            <div ref={ titlebarContainerRef } className={ titlebarContainerClasses.join(` `) }>
              <div className={ titlebarClasses.join(` `) }>
                <Title className={ `${ CLASS_PREFIX }__title` }>
                  { title }
                </Title>
                <ButtonClose className={ `${ CLASS_PREFIX }__close` } onClick={ onClose }/>
              </div>
            </div>
            <div className={ contentClasses.join(` `) }>
              { children }
            </div>
          </div>
        </div>
      </DialogContext.Provider>
    </dialog>
  )
}

export default Dialog;
